import React, { useState, useEffect } from "react";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";

const mapContainerStyle = {
  width: "100%",
  height: "550px",
};

const getMarkerColor = (device) => {
  if (device.status === "outage") return "red";
  if (device.status === "offline") return "blue";

  const v1 = device.voltageL1 || 120;
  const v2 = device.voltageL2 || 120;
  const low = 108;
  const high = 132;
  const blinkedRecently = device.blinkTracker > 0;

  if (v1 < low || v1 > high || v2 < low || v2 > high) return "yellow";
  if (blinkedRecently) return "orange";

  return "green";
};

const Map = ({ devices }) => {
  const [geocodedDevices, setGeocodedDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const geocodeMissingDevices = (deviceList) => {
    if (!window.google?.maps?.Geocoder) return;

    const geocoder = new window.google.maps.Geocoder();

    deviceList.forEach((device) => {
      if (!device.lat && device.address) {
        geocoder.geocode({ address: device.address }, (results, status) => {
          if (status === "OK" && results[0]) {
            const { lat, lng } = results[0].geometry.location;
            const enriched = { ...device, lat: lat(), lon: lng() };
            setGeocodedDevices((prev) => [...prev, enriched]);
          }
        });
      }
    });
  };

  useEffect(() => {
    if (devices.length > 0) geocodeMissingDevices(devices);
  }, [devices]);

  const allDevices = [
    ...devices.filter((d) => d.lat && d.lon),
    ...geocodedDevices,
  ];

  const getMapCenter = () => {
    if (allDevices.length === 0) return { lat: 39.8, lng: -88.4 };

    const avgLat = allDevices.reduce((sum, d) => sum + d.lat, 0) / allDevices.length;
    const avgLng = allDevices.reduce((sum, d) => sum + d.lon, 0) / allDevices.length;

    return { lat: avgLat, lng: avgLng };
  };

  return (
    <GoogleMap mapContainerStyle={mapContainerStyle} center={getMapCenter()} zoom={9}>
      {allDevices.map((device) => (
        <Marker
          key={device.deviceId}
          position={{ lat: device.lat, lng: device.lon }}
          icon={{
            url: `http://maps.google.com/mapfiles/ms/icons/${getMarkerColor(device)}-dot.png`,
            scaledSize: new window.google.maps.Size(40, 40),
          }}
          onClick={() => setSelectedDevice(device)}
        />
      ))}

      {selectedDevice && (
        <InfoWindow
          position={{ lat: selectedDevice.lat, lng: selectedDevice.lon }}
          onCloseClick={() => setSelectedDevice(null)}
        >
          <div style={{ fontSize: "14px", lineHeight: "1.5" }}>
            <strong>Device ID:</strong> {selectedDevice.deviceId}<br />
            <strong>Meter:</strong> {selectedDevice.meter || selectedDevice.meterNumber || "—"}<br />
            <strong>Account:</strong> {selectedDevice.account || selectedDevice.accountNumber || "—"}<br />
            <strong>Status:</strong> {selectedDevice.status}<br />
            <strong>Phase:</strong> {selectedDevice.phase || "Unknown"}<br />
            <strong>Battery:</strong> {selectedDevice.battery || "—"}<br />
            <strong>Voltage L1:</strong> {selectedDevice.voltageL1 || "—"}<br />
            <strong>Voltage L2:</strong> {selectedDevice.voltageL2 || "—"}<br />
            <strong>Last Seen:</strong><br />
            {selectedDevice.lastSeen
              ? new Date(selectedDevice.lastSeen).toLocaleString()
              : "—"}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default Map;
